"use client";
import {
  ChevronsUpDown,
  LogOut,
  // Moon, Palette, Sun
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  // DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarMenuButton } from "@/components/ui/sidebar";
import { NavigationMenuItem } from "./ui/navigation-menu";
import { singOutUser } from "@/app/actions/signOutUser";
import { useActionQueueContext } from "@/providers/action-queue-provider";
// import { Switch } from "./ui/switch";
// import { useTheme } from "next-themes";

const getFullNameInitials = (fullname: string) => {
  const nameParts = fullname.split(" ");

  if (nameParts.length === 1) {
    return `${fullname[0]}${fullname[1]}`.toUpperCase();
  }

  if (nameParts.length > 1) {
    return `${nameParts?.[0]?.[0]}${nameParts?.[1]?.[0]}`.toUpperCase();
  }
  return "NA";
};

export function NavUser({
  user,
}: {
  user: {
    company: string;
    user_fullname: string;
    avatar?: string;
    mpid?: string;
    group_mpid?: string;
  };
}) {
  const logActivity = useActionQueueContext();
  // const { theme, setTheme } = useTheme();

  return (
    <NavigationMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger
          asChild
          onClick={() => {
            logActivity({
              action: `site_navigation_menu.user_profile.clicked`,
              info: "User tried to open profile dropdown",
              unstructured_json: {},
            });
          }}
        >
          <SidebarMenuButton
            size="lg"
            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
          >
            <Avatar className="h-8 w-8 rounded-full">
              {typeof user.avatar === "string" ? (
                <AvatarImage src={user.avatar} />
              ) : null}
              <AvatarFallback className="bold rounded-lg text-black">
                <strong>{getFullNameInitials(user.user_fullname)}</strong>
              </AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="flex gap-2 truncate font-semibold">
                <span>{user.company}</span>
                <span>/</span>
                <span>{user.user_fullname}</span>
              </span>
              {user.group_mpid ? (
                <span className="flex items-center gap-2 truncate text-xs">
                  <span className="font-light">Group MPID: </span>
                  <span className="font-semibold">{user.group_mpid}</span>
                </span>
              ) : null}
              {user.mpid ? (
                <span className="flex items-center gap-2 truncate text-xs">
                  <span className="font-light">MPID: </span>
                  <span className="font-semibold">{user.mpid}</span>
                </span>
              ) : null}
            </div>
            <ChevronsUpDown className="ml-auto size-4" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
          side={"bottom"}
          align="end"
          sideOffset={4}
        >
          <DropdownMenuLabel className="p-0 font-normal">
            <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
              <Avatar className="h-8 w-8 rounded-full">
                {typeof user.avatar === "string" ? (
                  <AvatarImage src={user.avatar} />
                ) : null}
                <AvatarFallback className="rounded-lg">
                  {getFullNameInitials(user.user_fullname)}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="flex gap-2 truncate font-semibold">
                  <span>{user.company}</span>
                  <span>/</span>
                  <span>{user.user_fullname}</span>
                </span>
                {user.group_mpid ? (
                  <span className="truncate text-xs">
                    <span className="font-light">Group MPID: </span>
                    <span className="font-semibold">{user.group_mpid}</span>
                  </span>
                ) : null}
                {user.mpid ? (
                  <span className="truncate text-xs">
                    <span className="font-light">MPID: </span>
                    <span className="font-semibold">{user.mpid}</span>
                  </span>
                ) : null}
              </div>
            </div>
          </DropdownMenuLabel>
          {/* <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <div className="flex flex-row justify-between px-2 py-1.5">
              <div className="flex select-none flex-row items-center gap-2 text-sm [&>svg]:size-4 [&>svg]:shrink-0">
                <Palette />
                Theme
              </div>
              <div className="flex select-none flex-row items-center gap-2 text-sm [&>svg]:size-4 [&>svg]:shrink-0">
                <Moon />
                <Switch
                  checked={theme === "light"}
                  onCheckedChange={(checked) => {
                    setTheme(checked ? "light" : "dark");
                  }}
                />
                <Sun />
              </div>
            </div>
          </DropdownMenuGroup> */}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => {
              logActivity({
                action: `site_navigation_menu.logout.clicked`,
                info: "User tried to log out",
                unstructured_json: {},
              });
              void singOutUser();
            }}
          >
            <LogOut />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </NavigationMenuItem>
  );
}
